/**
 * Módulo: Data Service - Queries Otimizadas para Estoque
 * Arquivo: js/pages/suprimentos/estoque/data-service.js
 * 
 * Responsável por todas as interações com o banco de dados
 * Otimizado para minimizar queries e melhorar performance
 */

window.FazendaRP = window.FazendaRP || {};
window.FazendaRP.Estoque = window.FazendaRP.Estoque || {};
window.FazendaRP.Estoque.DataService = {

    /**
     * Busca ou cria registro de estoque para um item
     * 
     * @param {string} itemNome - Nome do item
     * @param {string|null} itemId - ID opcional do item no catálogo
     * @param {string} unidade - Unidade de medida
     * @param {number} custoInicial - Custo inicial (para criação)
     * @returns {Promise<Object>} Registro de estoque { id, item_nome, saldo_atual, custo_medio, ... }
     */
    buscarOuCriarEstoque: async function (itemNome, itemId = null, unidade = 'UN', custoInicial = 0) {
        try {
            // 1. Buscar existente (case-insensitive)
            let query = window.FazendaRP.supabase
                .from('suprimentos_estoque')
                .select('*');

            // Priorizar busca por ID se disponível
            if (itemId) {
                query = query.eq('item_id', itemId);
            } else {
                query = query.ilike('item_nome', itemNome);
            }

            const { data: existente, error: findErr } = await query.maybeSingle();

            if (findErr) throw findErr;

            // 2. Se existe, retornar
            if (existente) {
                console.log(`[DataService] Estoque encontrado: ${existente.item_nome} (ID: ${existente.id})`);
                return existente;
            }

            // 3. Se não existe, criar
            console.log(`[DataService] Criando novo estoque: ${itemNome}`);

            const { data: novo, error: createErr } = await window.FazendaRP.supabase
                .from('suprimentos_estoque')
                .insert([{
                    item_id: itemId,
                    item_nome: itemNome,
                    unidade: unidade,
                    saldo_atual: 0,
                    custo_medio: custoInicial
                }])
                .select()
                .single();

            if (createErr) throw createErr;

            console.log(`[DataService] Estoque criado com sucesso: ${novo.item_nome} (ID: ${novo.id})`);
            return novo;

        } catch (error) {
            console.error('[DataService] Erro em buscarOuCriarEstoque:', error);
            throw error;
        }
    },

    /**
     * Busca todas as movimentações de um item em ordem cronológica
     * 
     * @param {string} estoqueId - ID do registro de estoque
     * @returns {Promise<Array>} Lista de movimentações ordenadas
     */
    buscarMovimentacoes: async function (estoqueId) {
        try {
            const { data, error } = await window.FazendaRP.supabase
                .from('suprimentos_movimentacoes')
                .select('*')
                .eq('estoque_id', estoqueId)
                .order('data_movimentacao', { ascending: true })
                .order('created_at', { ascending: true }); // Desempate

            if (error) throw error;

            console.log(`[DataService] ${data?.length || 0} movimentações encontradas para estoque ${estoqueId}`);
            return data || [];

        } catch (error) {
            console.error('[DataService] Erro em buscarMovimentacoes:', error);
            throw error;
        }
    },

    /**
     * Busca custos de múltiplos lançamentos financeiros de uma vez
     * Otimização: Evita N+1 queries
     * 
     * @param {Array<string>} lancamentoIds - Lista de IDs de lançamentos
     * @returns {Promise<Object>} Mapa { lancamentoId_itemNome: custo }
     */
    buscarCustosLancamentos: async function (lancamentoIds) {
        try {
            if (!lancamentoIds || lancamentoIds.length === 0) {
                return {};
            }

            const custoMap = {};

            const { data, error } = await window.FazendaRP.supabase
                .from('financeiro_lancamentos')
                .select('id, detalhes')
                .in('id', lancamentoIds);

            if (error) throw error;

            // Helper para processar números que podem vir formatados do financeiro (ex: "7,49" ou "R$ 7,49")
            const parseNum = (v) => {
                if (typeof v === 'number') return v;
                if (!v) return 0;
                let clean = String(v).replace('R$', '').trim();
                clean = clean.replace(/[^\d.,-]/g, '');
                if (clean.includes(',') && clean.includes('.')) {
                    clean = clean.replace(/\./g, '').replace(',', '.');
                } else if (clean.includes(',')) {
                    clean = clean.replace(',', '.');
                }
                return parseFloat(clean) || 0;
            };

            (data || []).forEach(lanc => {
                if (lanc.detalhes?.itens) {
                    lanc.detalhes.itens.forEach(item => {
                        // CORREÇÃO: Aceitar tanto 'item_nome' (padrão estoque) quanto 'nome' (padrão financeiro)
                        const nomeOriginal = item.item_nome || item.nome || '';
                        const safeName = nomeOriginal.trim().toUpperCase();
                        const key = `${lanc.id}_${safeName}`;

                        // CORREÇÃO: Usar parseNum em vez de parseFloat direto para suportar formato PT-BR
                        const rawUnit = item.valor_unitario || item.valorUnitario;
                        const rawTotal = item.total || item.valorTotal;
                        const rawQtd = item.qtd || item.quantidade;

                        let custo = parseNum(rawUnit);

                        // Fallback: Se o unitário estiver zerado mas o total existir, calcular
                        if (custo === 0 && parseNum(rawTotal) > 0 && parseNum(rawQtd) > 0) {
                            custo = parseNum(rawTotal) / parseNum(rawQtd);
                        }

                        custoMap[key] = custo;
                    });
                }
            });

            console.log(`[DataService] Custos mapeados para ${Object.keys(custoMap).length} itens de ${lancamentoIds.length} lançamentos`);
            return custoMap;

        } catch (error) {
            console.error('[DataService] Erro em buscarCustosLancamentos:', error);
            return {};
        }
    },

    /**
     * Insere nova movimentação
     * 
     * @param {Object} movimentacao - Dados da movimentação
     * @returns {Promise<Object>} Movimentação criada
     */
    inserirMovimentacao: async function (movimentacao) {
        try {
            const payload = {
                estoque_id: movimentacao.estoqueId,
                item_nome: movimentacao.itemNome,
                tipo: movimentacao.tipo,
                quantidade: parseFloat(movimentacao.quantidade),
                origem_tipo: movimentacao.origemTipo,
                origem_id: movimentacao.origemId || null,
                observacao: movimentacao.observacao || '',
                custo_unitario: movimentacao.custo_unitario, // Fix: Persist Snapshot Cost
                data_movimentacao: movimentacao.dataOperacao || new Date().toISOString().split('T')[0],
                created_at: new Date().toISOString()
            };

            const { data, error } = await window.FazendaRP.supabase
                .from('suprimentos_movimentacoes')
                .insert([payload])
                .select()
                .single();

            if (error) throw error;

            console.log(`[DataService] Movimentação criada: ${data.tipo} ${data.quantidade} ${movimentacao.itemNome}`);
            return data;

        } catch (error) {
            console.error('[DataService] Erro em inserirMovimentacao:', error);
            throw error;
        }
    },

    /**
     * Atualiza saldo e custo médio do estoque
     * 
     * @param {string} estoqueId - ID do registro de estoque
     * @param {number} novoSaldo - Novo saldo
     * @param {number} novoCusto - Novo custo médio
     * @returns {Promise<void>}
     */
    atualizarEstoque: async function (estoqueId, novoSaldo, novoCusto) {
        try {
            const { error } = await window.FazendaRP.supabase
                .from('suprimentos_estoque')
                .update({
                    saldo_atual: novoSaldo,
                    custo_medio: novoCusto,
                    updated_at: new Date().toISOString()
                })
                .eq('id', estoqueId);

            if (error) throw error;

            console.log(`[DataService] Estoque atualizado: Saldo=${novoSaldo.toFixed(2)}, Custo=${novoCusto.toFixed(2)}`);

        } catch (error) {
            console.error('[DataService] Erro em atualizarEstoque:', error);
            throw error;
        }
    },

    /**
     * Deleta movimentação (apenas para manutenção/correção)
     * 
     * @param {string} movimentacaoId - ID da movimentação
     * @returns {Promise<void>}
     */
    deletarMovimentacao: async function (movimentacaoId) {
        try {
            const { error } = await window.FazendaRP.supabase
                .from('suprimentos_movimentacoes')
                .delete()
                .eq('id', movimentacaoId);

            if (error) throw error;

            console.log(`[DataService] Movimentação deletada: ${movimentacaoId}`);

        } catch (error) {
            console.error('[DataService] Erro em deletarMovimentacao:', error);
            throw error;
        }
    }
};
